import { useState, type FormEvent } from "react";
import { ActionIcon, Badge, Button, Card, Combobox, Input } from "@insights/ui";
import { KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { useParams } from "react-router";
import { api, authClient, json } from "../../../api";
import { PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import {
  useAdministration,
  type TokenPermission,
} from "../../workspace-administration";
import { getPasskeyRegistrationError } from "../helpers/get-passkey-registration-error";

const tokenPermissionOptions = [
  { value: "read", label: "Read only" },
  { value: "contributor", label: "Contributor" },
] as const;

export function SecurityPage() {
  const { workspaceId } = useParams();
  const { query } = useAdministration(workspaceId);
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [passkeyError, setPasskeyError] = useState("");
  const [passkeySuccess, setPasskeySuccess] = useState("");
  const [tokenName, setTokenName] = useState("");
  const [tokenPermission, setTokenPermission] =
    useState<TokenPermission>("read");
  const [newToken, setNewToken] = useState<string>();

  async function addToken(event: FormEvent) {
    event.preventDefault();
    const result = await api<{ token: string }>(
      `/${workspaceId}/tokens`,
      json({ name: tokenName, permission: tokenPermission }),
    );
    setNewToken(result.token);
    setTokenName("");
    await navigator.clipboard.writeText(result.token).catch(() => undefined);
    await query.refetch();
  }

  async function revokeToken(token: { id: string; name: string }) {
    if (
      !confirm(
        `Revoke the ${token.name} token? Terrabuild clients using it will immediately lose access.`,
      )
    )
      return;
    await api(`/${workspaceId}/tokens/${token.id}`, { method: "DELETE" });
    await query.refetch();
  }

  async function addPasskey() {
    setPasskeyError("");
    setPasskeySuccess("");

    if (!("PublicKeyCredential" in window)) {
      setPasskeyError("This browser does not support passkeys.");
      return;
    }

    setPasskeyPending(true);

    try {
      const result = await authClient.passkey.addPasskey({
        name: `Insights ${new Date().toLocaleDateString()}`,
      });

      if (result.error) {
        setPasskeyError(getPasskeyRegistrationError(result.error));
        return;
      }

      setPasskeySuccess("This device is now registered.");
    } catch (error) {
      setPasskeyError(
        error instanceof Error
          ? error.message
          : "Unable to register this device.",
      );
    } finally {
      setPasskeyPending(false);
    }
  }

  const canManageTokens = ["owner", "admin"].includes(query.data?.role ?? "");
  return (
    <AppShell>
      <PageHeader eyebrow="Credentials & integration" title="Security" />
      <div className="admin-grid">
        <Card className="form-card wide-card">
          <h2 className="section-card-title">Passkeys</h2>
          <p>
            Register multiple passkeys from trusted devices. Sessions and
            passkeys can be revoked through the account API.
          </p>
          <div className="passkey-action">
            <Button
              disabled={passkeyPending}
              icon={<ShieldCheck />}
              onClick={addPasskey}
            >
              {passkeyPending ? "Registering…" : "Register this device"}
            </Button>
          </div>
          <div className="passkey-status" aria-live="polite">
            {passkeyError && <p className="error">{passkeyError}</p>}
            {passkeySuccess && (
              <p className="passkey-success">{passkeySuccess}</p>
            )}
          </div>
        </Card>
        <Card className="form-card wide-card token-card">
          <h2 className="section-card-title">Terrabuild tokens</h2>
          {canManageTokens ? (
            <form className="token-form" onSubmit={addToken}>
              <Input
                value={tokenName}
                onChange={(event) => setTokenName(event.target.value)}
                placeholder="CI production"
                maxLength={100}
                required
              />
              <Combobox
                label="Token permission"
                value={tokenPermission}
                options={tokenPermissionOptions}
                onValueChange={setTokenPermission}
              />
              <Button icon={<KeyRound />}>Generate token</Button>
            </form>
          ) : (
            <p className="muted">
              Only workspace owners and administrators can manage tokens.
            </p>
          )}
          {newToken && (
            <>
              <p className="token-created">
                Copy this token now. It will not be displayed again.
              </p>
              <code className="secret">{newToken}</code>
            </>
          )}
          {query.data?.tokens.map((token) => (
            <div className="row" key={token.id}>
              <div>
                <strong>{token.name}</strong>
                <span>{token.revoked_at ? "Revoked" : "Active"}</span>
              </div>
              <div className="token-actions">
                <Badge
                  className={
                    token.permission === "contributor" ? "success" : ""
                  }
                >
                  {token.permission === "contributor"
                    ? "Contributor"
                    : "Read only"}
                </Badge>
                {canManageTokens && !token.revoked_at && (
                  <ActionIcon
                    color="danger"
                    icon={<Trash2 />}
                    label={`Revoke ${token.name}`}
                    onClick={() => revokeToken(token)}
                    size="small"
                  />
                )}
              </div>
            </div>
          ))}
        </Card>
      </div>
    </AppShell>
  );
}
